import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { ButtonGroup } from "@replohq/lattice/components/button/ButtonGroup";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import LogoBadge from "@v2/components/LogoBadge";
import { getProjectName } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import { Paintbrush, Settings } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

export const AgentProjectSelectorHeaderSkeleton = () => {
  return <Skeleton className="w-full h-10" />;
};

export const AgentProjectSelectorHeader = ({
  currentProject,
  workspaceName,
  onPersonalize,
}: {
  currentProject?: ReploProject;
  workspaceName: string;
  onPersonalize?: () => void;
}) => {
  const navigate = useNavigate();

  const projectName = currentProject
    ? getProjectName(currentProject)
    : workspaceName;

  const handleSettingsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!currentProject) {
      return;
    }
    navigate(
      generatePath(routes.shops.settings.shops.general, {
        projectId: currentProject.id,
      }),
    );
  };

  const handlePersonalizeClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onPersonalize?.();
  };

  return (
    <ButtonGroup>
      <Button
        render={<div />}
        nativeButton={false}
        variant="secondary"
        size="base"
        UNSAFE_className="font-medium [[data-popup-open]_&]:bg-button-tertiary-hover"
        startEnhancer={<LogoBadge className="size-4.5!" />}
      >
        {projectName}
      </Button>
      <Button
        render={<div />}
        nativeButton={false}
        icon
        variant="secondary"
        size="base"
        aria-label="Project settings"
        onClick={handleSettingsClick}
      >
        <Settings />
      </Button>
      {onPersonalize && (
        <Button
          render={<div />}
          nativeButton={false}
          icon
          variant="secondary"
          size="base"
          aria-label="Personalize"
          onClick={handlePersonalizeClick}
        >
          <Paintbrush />
        </Button>
      )}
    </ButtonGroup>
  );
};
